import { ModuleBase, UserContext } from '@/components';
import {
  CognitiveImageSearchRequestModel,
  CognitiveSearchResultModel,
  ImageLibrarySearchResult,
  ModuleBaseModel,
  ModuleBasePropsModel,
} from '@/types';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReportsSearchFilterType } from '@/components/search/SearchFilters';
import ImageSearchIcon from '@/assets/images/image-search-icon.png';
import ImageLibraryFilter from './ImageLibraryFilter';
import ImageLibraryResult from './ImageLibraryResult';
import { useContext, useEffect, useRef, useState } from 'react';
import { getSearchFilters } from '@/utils';
import { searchImages } from '@/api';

export type ImageLibraryModuleModel = ModuleBaseModel<
  {
    availableFilters: ReportsSearchFilterType[];
    requestFilters: {
      facetsMode: 'IncludeFacets';
      orderBy: 'score' | 'date';
      page: number;
      pageSize: number;
      publicSiteSearch: boolean;
      searchTerm: string;
    };
  } & ModuleBasePropsModel
>;

const ImageLibraryModule = (data: ImageLibraryModuleModel) => {
  const {
    props: { availableFilters, requestFilters },
  } = data;
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const user = useContext(UserContext);

  const [result, setResult] = useState<CognitiveSearchResultModel<ImageLibrarySearchResult>>({
    facets: [],
    results: [],
    totalResults: 0,
  });
  const isFetchingRef = useRef(false);
  const router = useRouter();

  const loadMore = () => {
    if (!isFetchingRef.current) {
      isFetchingRef.current = true;
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    setResult((current) => ({
      facets: current.facets,
      results: [],
      totalResults: 0,
    }));
    setPage(0);
    if (page === 0) {
      setTimeout(() => {
        if (!isFetchingRef.current) {
          getData();
        }
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  useEffect(() => {
    //if (!isFetchingRef.current) {
    getData();
    //}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const getData = () => {
    setLoading(true);
    isFetchingRef.current = true;
    const filtersNameMap: { [key: string]: string } = {};
    availableFilters.forEach((filter) => {
      filtersNameMap[filter.key] = filter.key;
    });

    const filters = getSearchFilters(router, filtersNameMap);

    const isCatwalk = Array.isArray(router.query.searchType)
      ? router.query.searchType[0] === 'catwalks'
      : router.query.searchType === 'catwalks';

    const filterRequest: CognitiveImageSearchRequestModel = {
      ...requestFilters,
      facetsMode: 'IncludeFacets',
      searchTerm: Array.isArray(router.query.searchTerm) ? router.query.searchTerm[0] : router.query.searchTerm || '*',
      filters,
      pageSize: 20,
      isCatwalk,
      page: page,
      orderBy: Array.isArray(router.query.orderBy) ? router.query.orderBy[0] : router.query.orderBy || 'score',
      account: user?.account,
      userId: user?.userId,
    };

    /**
     * If the service is returning 401 unauthorized, it means the token is invalid for the environment.
     * In your .env file, make sure you have the correct AZURE_B2C_CLIENT_ID, AZURE_B2C_CLIENT_SECRET, AZURE_B2C_DOMAIN_NAME, AZURE_B2C_POLICY_NAME and AZURE_B2C_SCOPES for the environment you are working on (UAT or PROD)
     */

    searchImages(filterRequest)
      .then((dataResponse) => {
        /*
        const duplicates = new Set();
        const seen = new Set();

        [...result.results, ...dataResponse.results].forEach((item) => {
          if (seen.has(item.media.mediaId)) {
            duplicates.add(item.media.mediaId);
          } else {
            seen.add(item.media.mediaId);
          }
        });

        [...result.results, ...dataResponse.results].forEach((item) => {
          if (duplicates.has(item.media.mediaId)) {
            console.warn('Duplicate mediaId found:', item.media.imageUrl);
          }
        });

        console.log('Duplicate mediaIds in results:', Array.from(duplicates));
*/
        setResult((prev) => ({
          facets: dataResponse.facets.map((facet) => ({
            ...facet,
            name: availableFilters.find((f) => f.key === facet.name)?.name || facet.name,
          })),
          results: [
            ...prev.results,
            ...dataResponse.results.map((item, index) => ({ ...item, index: prev.results.length + index })),
          ],
          totalResults: dataResponse.totalResults,
        }));
      })
      .finally(() => {
        setLoading(false);
        isFetchingRef.current = false;
      });
  };

  return (
    <ModuleBase
      data={{ ...data, props: { ...data.props, backgroundColour: '#eeeeee' } }}
      noPadding
      className="flex min-h-svh flex-col items-center justify-start"
    >
      <div className="flex w-full max-w-[1800px] flex-col md:px-3 xl:px-6">
        <div className="container">
          <div className="mt-12 mb-6 flex w-full items-center justify-center">
            <Image src={ImageSearchIcon} alt="Image Search" className="w-[40px]" priority />
            <div className="ml-3 text-2xl font-semibold text-[#E63946] md:mb-1 md:text-[30px] md:leading-[32px]">
              Image Library
            </div>
          </div>
          <ImageLibraryFilter filters={availableFilters} facets={result.facets} />
        </div>
        <ImageLibraryResult result={result} onLoadMore={loadMore} loading={loading} />
      </div>
    </ModuleBase>
  );
};

export default ImageLibraryModule;
